import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import TextLink from 'LuxKit/TextLink'
import Tooltip from 'LuxKit/Tooltip'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import { tierGradientBackgroundStyle, tierNormalContrastBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import withMountGuard from 'hocs/withMountGuard'
import { useAppSelector } from 'hooks/reduxHooks'
import { arrayToObject, nonNullable } from 'lib/array/arrayUtils'
import { clamp } from 'lib/maths/mathUtils'
import LoyaltyAccountTileCircularProgressBar from 'loyalty/components/LoyaltyAccountTileCircularProgressBar'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import LoyaltyDrawerTierCard from 'loyalty/components/LoyaltyDrawerTierCard'
import LoyaltyDrawerCongratulationsBanner from 'loyalty/components/LoyaltyDrawerCongratulationsBanner'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useCurrentLoyaltyPendingPoints from 'loyalty/hooks/useCurrentLoyaltyPendingPoints'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import AccountDrawerBenefitHighlightItem from './AccountDrawerBenefitHighlightItem'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import CopyToClipboardClickableIcon from 'checkout/Components/Confirmation/BookingDetailsV2/CopyToClipboardClickableIcon'
import { FormattedMessage, useIntl } from 'react-intl'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import { TasteOfGoldPromoCard } from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoStyles'
import TasteOfGoldPromoContent from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoContent'

const Container = styled(Clickable)`
  padding: ${rem(12)} ${rem(16)};
  color: ${props => props.theme.palette.product.luxPlus.contrast};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}

  &:focus {
    ${interactiveFocusStyle}
  }
`

const ProgressBarsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-self: stretch;
  padding-bottom: ${rem(4)};
`

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  line-height: 1;
`

const TierDivider = styled(Divider)`
  opacity: 0.5;
  ${tierNormalContrastBackgroundStyle}
`

const TIER_BENEFITS: Record<App.LoyaltyTier, Array<App.LoyaltyBenefitType>> = {
  bronze: [],
  silver: ['hotel_upgrade', 'lounge_pass', 'discounted_insurance'],
  gold: ['hotel_upgrade', 'airport_transfer', 'lounge_pass'],
  platinum: ['airport_transfer', 'hotel_upgrade', 'lounge_pass'],
}

const BENEFIT_ICONS: Utils.PartialRecord<App.LoyaltyBenefitType, React.ReactElement> = {
  lounge_pass: <LineChairWithAirplaneIcon />,
  airport_transfer: <LineTaxiIcon />,
  hotel_upgrade: <LineCrownIcon />,
  discounted_insurance: <LineShieldCheckIcon />,
}

interface BenefitConfigItem {
  code: App.LoyaltyBenefitType
  displayInfo: {
    title: string
    icon: React.ReactElement
  }
}

function getTierBenefitDisplayInfo(tier: App.LoyaltyTier, benefitTitles: Utils.PartialRecord<App.LoyaltyBenefitType, string>): Array<BenefitConfigItem> {
  const tierBenefits = TIER_BENEFITS[tier]
  const displayInfoItems = tierBenefits.map(code => {
    const icon = BENEFIT_ICONS[code]
    const title = benefitTitles[code]
    return icon && title ? { code, displayInfo: { title, icon } } : undefined
  })
  return nonNullable(displayInfoItems)
}

interface Props {
  onLinkClick: () => void
  loyaltyAccountData: App.LoyaltyAccount
}

function AccountMenuLoyaltyHasAccountDetailsTile({ onLinkClick, loyaltyAccountData }: Props) {
  const tier = loyaltyAccountData.tier
  const benefits = loyaltyAccountData.benefits
  const points = loyaltyAccountData.points
  const membershipNumber = `${loyaltyAccountData.accountNumber}`
  const intl = useIntl()

  const [, , nextTier] = useCurrentLoyaltyTier()
  const [pendingPoints] = useCurrentLoyaltyPendingPoints()

  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)

  const benefitTitles: Utils.PartialRecord<App.LoyaltyBenefitType, string> = {
    lounge_pass: intl.formatMessage({
      id: 'nstaLcaEql',
      defaultMessage: 'Free airport lounge passes',
      description: 'Loyalty benefit title for complimentary airport lounge access',
    }),
    airport_transfer: intl.formatMessage({
      id: 'a/r/yRtiFB',
      defaultMessage: 'Free airport chauffeur service',
      description: 'Loyalty benefit title for complimentary airport chauffeur transfer',
    }),
    hotel_upgrade: intl.formatMessage({
      id: 'cpTdAkrvd+',
      defaultMessage: 'Free room or package upgrade',
      description: 'Loyalty benefit title for complimentary room or package upgrade',
    }),
    discounted_insurance: intl.formatMessage({
      id: 'mnLcU6wWtt',
      defaultMessage: 'Discounted travel insurance',
      description: 'Loyalty benefit title for discounted travel insurance',
    }),
  }

  const membershipLabel = intl.formatMessage(
    {
      id: 'EmBa2cCCza',
      defaultMessage: 'Membership {membershipNumber}',
      description: 'Loyalty membership number display in account menu element',
    },
    { membershipNumber },
  )
  const copyMembershipNumberLabel = intl.formatMessage({
    id: 'gkoIBvPTDx',
    defaultMessage: 'Copy membership number',
    description: 'Aria label for button to copy loyalty membership number',
  })
  const ptsLabel = 'pts'
  const statusCreditsLabel = intl.formatMessage({
    id: '+EhO0xPNWF',
    defaultMessage: 'Status Credits',
    description: 'Label for status credits progress in loyalty account tile',
  })
  const statusCreditsPendingTooltip = intl.formatMessage({
    id: 'X9h8QWFz+O',
    defaultMessage: 'Pending status credits may take up to 24hrs to appear',
    description: 'Tooltip explaining pending status credits delay in loyalty account tile',
  })
  const nightsLabel = intl.formatMessage({
    id: 'x5UY9HsF8S',
    defaultMessage: 'Nights',
    description: 'Form field label for nights progress in loyalty account tile',
  })
  const nightsPendingTooltip = intl.formatMessage({
    id: 'Z4G3K4vMzZ',
    defaultMessage: 'Pending nights may take up to 24hrs to appear',
    description: 'Tooltip explaining pending nights delay in loyalty account tile',
  })
  const topBenefitsLabel = intl.formatMessage({
    id: 'Q1EO2NcxyI',
    defaultMessage: 'Your top benefits',
    description: 'Section heading for top loyalty membership benefits',
  })
  const viewMembershipLabel = intl.formatMessage({
    id: 'wx0sQZ1Mxh',
    defaultMessage: 'View membership',
    description: 'Hyperlink text to view full loyalty membership details',
  })

  const benefitsMap = arrayToObject(benefits, benefit => benefit.code)
  const tierBenefitsDisplayInfo = getTierBenefitDisplayInfo(tier, benefitTitles)

  const validBenefitsDisplayInfo = nonNullable(
    tierBenefitsDisplayInfo.map(displayInfo =>
      benefitsMap[displayInfo.code] ? displayInfo : undefined,
    ),
  )

  const handleCopyClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  // Tier progression mode (platinum users are in retain mode)
  const isRetainMode = !nextTier
  const unlockCardTier = nextTier ?? tier

  // Current progress values
  const currentStatusCredits = loyaltyAccountData.statusCredits
  const currentNights = loyaltyAccountData.numberOfNights

  // Target values based on mode (retain vs upgrade)
  const targetCredits = isRetainMode
    ? loyaltyAccountData.totalCreditsForTierRetention
    : loyaltyAccountData.totalCreditsForTierUpgrade
  const targetNights = isRetainMode
    ? loyaltyAccountData.numberOfNightsForTierRetention
    : loyaltyAccountData.numberOfNightsForTierUpgrade

  // Remaining values to reach target
  const creditsRemaining = Math.max(targetCredits - currentStatusCredits, 0)
  const nightsRemaining = Math.max(targetNights - currentNights, 0)

  // Platinum user has retained their tier if they've met all requirements
  const hasRetainedTier = isRetainMode && creditsRemaining === 0 && nightsRemaining === 0

  // Calculate progress for circular bars
  const statusCreditsProgress = useMemo(() => {
    if (targetCredits === 0) return 100
    const baseProgress = Math.floor((currentStatusCredits / targetCredits) * 100)
    return clamp(0, baseProgress, 100)
  }, [currentStatusCredits, targetCredits])

  const nightsProgress = useMemo(() => {
    if (targetNights === 0) return 100
    const baseProgress = Math.floor((currentNights / targetNights) * 100)
    return clamp(0, baseProgress, 100)
  }, [currentNights, targetNights])

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <Container
      to={`/${LOYALTY_LANDING_PAGE}`}
      onClick={onLinkClick}
      className={clsx(`tier-${tier}`)}
    >
      <VerticalSpacer gap={20}>
        <VerticalSpacer gap={4}>
          <Group direction="horizontal" horizontalAlign="space-between">
            <LoyaltyProgramNameLogo width={72} />
            {tier && <BodyText variant="medium" format="uppercase">{getLoyaltyTierDisplayName(tier)}</BodyText>}
          </Group>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <Caption variant="large">{membershipLabel}</Caption>
              <CopyToClipboardClickableIcon
                value={membershipNumber}
                onCopyClick={handleCopyClick}
                ariaLabel={copyMembershipNumberLabel}
              />
            </Group>
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <Heading variant="heading6">
                <FormatDecimal value={points} />
              </Heading>
              <Caption variant="medium" weight="normal">{ptsLabel}</Caption>
            </Group>
          </Group>
        </VerticalSpacer>

        {/* Circular progress bars */}
        <ProgressBarsContainer>
          <Group direction="vertical" horizontalAlign="center" gap={4}>
            <LoyaltyAccountTileCircularProgressBar
              progress={statusCreditsProgress}
              tier={tier}
            >
              <Group direction="vertical" horizontalAlign="center" gap={2}>
                <BodyText variant="medium" weight="bold">
                  <FormatDecimal value={currentStatusCredits} />
                </BodyText>
                <TierDivider className={`tier-${tier}`} kind="primary" />
                <Caption variant="small">
                  <FormatDecimal value={targetCredits} />
                </Caption>
              </Group>
            </LoyaltyAccountTileCircularProgressBar>
            <Group direction="vertical" horizontalAlign="center" gap={2}>
              <BodyText variant="small" weight="bold">{statusCreditsLabel}</BodyText>
              <Group direction="horizontal" gap={4} verticalAlign="center">
                <BodyText variant="small">
                  <FormattedMessage
                    id="4XTLBdhy0X"
                    defaultMessage="{value} pending"
                    description="Pending status credits count in loyalty account tile"
                    values={{ value: <FormatDecimal value={pendingPoints.statusCredits} /> }}
                  />
                </BodyText>
                <Tooltip description={statusCreditsPendingTooltip} placement="bottom">
                  <InfoIconWrapper><LineInfoCircleIcon size={14} /></InfoIconWrapper>
                </Tooltip>
              </Group>
            </Group>
          </Group>

          <Group direction="vertical" horizontalAlign="center" gap={4}>
            <LoyaltyAccountTileCircularProgressBar
              progress={nightsProgress}
              tier={tier}
            >
              <Group direction="vertical" horizontalAlign="center" gap={2}>
                <BodyText variant="medium" weight="bold">
                  <FormatDecimal value={currentNights} />
                </BodyText>
                <TierDivider className={`tier-${tier}`} kind="primary" />
                <Caption variant="small">
                  <FormatDecimal value={targetNights} />
                </Caption>
              </Group>
            </LoyaltyAccountTileCircularProgressBar>
            <Group direction="vertical" horizontalAlign="center" gap={2}>
              <BodyText variant="small" weight="bold">{nightsLabel}</BodyText>
              <Group direction="horizontal" gap={4} verticalAlign="center">
                <BodyText variant="small">
                  <FormattedMessage
                    id="m/QJ84bZlY"
                    defaultMessage="{value} pending"
                    description="Pending nights count in loyalty account tile in the loyalty section"
                    values={{ value: <FormatDecimal value={pendingPoints.nights} /> }}
                  />
                </BodyText>
                <Tooltip description={nightsPendingTooltip} placement="bottom">
                  <InfoIconWrapper><LineInfoCircleIcon size={14} /></InfoIconWrapper>
                </Tooltip>
              </Group>
            </Group>
          </Group>
        </ProgressBarsContainer>

        <VerticalSpacer gap={20}>
          {/* Taste of Gold promo section */}
          {isTasteOfGoldActive && <TasteOfGoldPromoCard><TasteOfGoldPromoContent layout="vertical" size="small" stopPropagation /></TasteOfGoldPromoCard>}

          {/* Tier card or congratulations banner */}
          {hasRetainedTier && (
            <LoyaltyDrawerCongratulationsBanner
              tier={tier}
              reviewDate={loyaltyAccountData.statusReviewDate}
            />
          )}
          {!hasRetainedTier && (
            <LoyaltyDrawerTierCard
              tier={unlockCardTier}
              isRetain={isRetainMode}
              creditsRemaining={creditsRemaining}
              nightsRemaining={nightsRemaining}
              expiryDate={loyaltyAccountData.statusReviewDate}
            />
          )}

          {/* Benefits section */}
          {validBenefitsDisplayInfo && validBenefitsDisplayInfo.length > 0 && (
            <VerticalSpacer gap={8}>
              <BodyText variant="small" weight="bold">{topBenefitsLabel}</BodyText>
              {validBenefitsDisplayInfo.map(({ code, displayInfo }, index) => (
                <React.Fragment key={code}>
                  {index > 0 && <Divider kind="secondary" />}
                  <AccountDrawerBenefitHighlightItem
                    benefit={benefitsMap[code]}
                    tier={tier}
                    title={displayInfo.title}
                    icon={displayInfo.icon}
                  />
                </React.Fragment>
              ))}
            </VerticalSpacer>
          )}

          <Group direction="horizontal" horizontalAlign="end">
            <TextLink size="small">
              {viewMembershipLabel}
            </TextLink>
          </Group>
        </VerticalSpacer>
      </VerticalSpacer>
    </Container>
  </ProductPaletteProvider>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(AccountMenuLoyaltyHasAccountDetailsTile)
